import { useLazyQuery } from '@apollo/client/react';
import { useEffect, useMemo } from 'react';

import {
  GetReceptionPaneAppointmentDocument,
  ReceptionPaneAppointmentFragment,
} from '~/graphql/generated/graphql';
import { getNode } from '~/graphql/utility';
import { usePreviousValue } from '~/hooks/use-previous-value';
import { useSubscriptions } from '~/hooks/use-subscriptions';

export const useFetchAppointment = (
  appointmentId: string,
): {
  loading: boolean;
  appointment?: ReceptionPaneAppointmentFragment;
} => {
  const [appointmentEvent, clearAppointmentEvent] = useSubscriptions('Appointment', ['updated']);
  const [uberDeliveryEvent, clearUberDeliveryEvent] = useSubscriptions('UberDelivery', ['updated']);

  const [getAppointment, { loading, data }] = useLazyQuery(GetReceptionPaneAppointmentDocument, {
    fetchPolicy: 'network-only',
  });

  const prevData = usePreviousValue(data);
  const currentData = data || prevData;
  const appointment = useMemo(() => {
    const _appointment = getNode(currentData, 'Appointment');

    if (_appointment?.id === appointmentId) {
      return _appointment;
    }
  }, [appointmentId, currentData]);

  useEffect(() => {
    if (appointmentId) {
      getAppointment({ variables: { appointmentId } });
    }
  }, [appointmentId, getAppointment]);

  useEffect(() => {
    if (!data) return;

    if (appointmentEvent) {
      clearAppointmentEvent();
    }
    if (uberDeliveryEvent) {
      clearUberDeliveryEvent();
    }
  }, [data, appointmentEvent, uberDeliveryEvent, clearAppointmentEvent, clearUberDeliveryEvent]);

  useEffect(() => {
    if (appointmentEvent?.actionName === 'updated' && appointmentEvent.typeId === appointmentId) {
      getAppointment({ variables: { appointmentId } });
    }
  }, [appointmentId, appointmentEvent?.actionName, appointmentEvent?.typeId, getAppointment]);

  useEffect(() => {
    if (
      uberDeliveryEvent?.actionName === 'updated' &&
      uberDeliveryEvent.typeId === appointment?.uberDelivery?.id
    ) {
      // UberDeliveryが更新された場合は、対象の受付に紐づくもののみ再取得
      getAppointment({ variables: { appointmentId } });
    }
  }, [
    appointmentId,
    appointment?.uberDelivery?.id,
    uberDeliveryEvent?.actionName,
    uberDeliveryEvent?.typeId,
    getAppointment,
  ]);

  return { loading, appointment };
};
